import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, Save, Handshake, Eye, MousePointerClick, TrendingUp } from "lucide-react";

const TIERS = [
  { value: "principal", label: "Principal (exclusivo)" },
  { value: "oro", label: "Oro" },
  { value: "plata", label: "Plata" },
  { value: "bronce", label: "Bronce / Nos apoyan" },
];
const PLACEMENTS = [
  { value: "hero", label: "Hero (presentado por)" },
  { value: "wall", label: "Muro (landing)" },
  { value: "footer", label: "Footer (todas las páginas)" },
  { value: "banner_galerias", label: "Banner en Galerías" },
  { value: "banner_plantel", label: "Banner en Plantel" },
  { value: "banner_tienda", label: "Banner en Tienda" },
  { value: "page", label: "Página de Patrocinadores" },
];
const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

function ctr(s) {
  const imp = s.impressions || 0, cl = s.clicks || 0;
  return imp ? ((cl / imp) * 100).toFixed(1) + "%" : "—";
}

export default function AdminSponsorsTab() {
  const [sponsors, setSponsors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoRef = useRef(null);

  const load = useCallback(async () => {
    const data = await api.get("/sponsors/all").then((x) => x.data).catch(() => []);
    setSponsors(data);
    return data;
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = sponsors.reduce(
    (a, s) => ({ imp: a.imp + (s.impressions || 0), cl: a.cl + (s.clicks || 0) }),
    { imp: 0, cl: 0 }
  );

  const createSponsor = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (!fd.get("name")) return;
    try {
      const { data } = await api.post("/sponsors", { name: fd.get("name"), tier: fd.get("tier") || "bronce" });
      e.target.reset();
      toast.success("Patrocinador creado");
      await load();
      setSelected(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  const saveSponsor = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/sponsors/${selected.id}`, {
        name: fd.get("name"), tier: fd.get("tier"), category: fd.get("category") || "",
        url: fd.get("url") || null, description: fd.get("description") || "",
        status: fd.get("status"), placements: fd.getAll("placements"),
        start_date: fd.get("start_date") || null, end_date: fd.get("end_date") || null,
        order: Number(fd.get("order") || 0),
        phone: fd.get("phone") || "", whatsapp: fd.get("whatsapp") || "",
        address: fd.get("address") || "", hours: fd.get("hours") || "",
        instagram: fd.get("instagram") || "", facebook: fd.get("facebook") || "",
        maps_url: fd.get("maps_url") || "", promotions: fd.get("promotions") || "",
      });
      setSelected(data);
      toast.success("Patrocinador guardado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const deleteSponsor = async (id) => {
    if (!window.confirm("¿Eliminar este patrocinador?")) return;
    await api.delete(`/sponsors/${id}`);
    if (selected?.id === id) setSelected(null);
    toast.success("Patrocinador eliminado");
    load();
  };

  const uploadLogo = async (file) => {
    if (!file || !selected) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/sponsors/${selected.id}/logo`, fd);
      setSelected(data); load();
      toast.success("Logo subido");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally { setUploading(false); if (logoRef.current) logoRef.current.value = ""; }
  };

  const uploadGallery = async (file) => {
    if (!file || !selected) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/sponsors/${selected.id}/gallery`, fd);
      setSelected(data); load();
      toast.success("Imagen agregada");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
  };

  const removeGalleryImage = async (img) => {
    const gallery = (selected.gallery || []).filter((x) => x !== img);
    const { data } = await api.patch(`/sponsors/${selected.id}`, { gallery });
    setSelected(data); load();
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Métricas globales */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-testid="sponsors-metrics">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-semibold"><Eye className="h-4 w-4" /> Impresiones</div>
          <div className="mt-1 text-2xl font-extrabold text-navy">{totals.imp.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-semibold"><MousePointerClick className="h-4 w-4" /> Clics</div>
          <div className="mt-1 text-2xl font-extrabold text-navy">{totals.cl.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-400 text-xs font-semibold"><TrendingUp className="h-4 w-4" /> CTR global</div>
          <div className="mt-1 text-2xl font-extrabold text-navy">{totals.imp ? ((totals.cl / totals.imp) * 100).toFixed(1) + "%" : "—"}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Crear + lista */}
        <section className="lg:col-span-1">
          <form onSubmit={createSponsor} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-navy font-bold"><Handshake className="h-5 w-5 text-gold" /> Nuevo patrocinador</div>
            <input name="name" data-testid="sponsor-name-input" placeholder="Nombre de la marca" className={`mt-4 ${field}`} />
            <select name="tier" data-testid="sponsor-tier-select" defaultValue="bronce" className={`mt-3 bg-white ${field}`}>
              {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button data-testid="sponsor-create-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-2.5 rounded-lg hover:bg-[#16235a]">
              <Plus className="h-4 w-4" /> Crear
            </button>
          </form>

          <div className="mt-5 space-y-2">
            {sponsors.map((s) => (
              <button key={s.id} data-testid={`sponsor-item-${s.slug || s.id}`} onClick={() => setSelected(s)}
                className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${selected?.id === s.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"}`}>
                <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#eef1f7] shrink-0 flex items-center justify-center">
                  {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-contain" /> : <Handshake className="h-5 w-5 text-navy/30" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  <div className={`text-xs flex items-center gap-2 ${selected?.id === s.id ? "text-gold" : "text-gray-400"}`}>
                    <span className="capitalize">{s.tier}</span>·
                    <span className="inline-flex items-center gap-0.5"><Eye className="h-3 w-3" />{s.impressions || 0}</span>
                    <span className="inline-flex items-center gap-0.5"><MousePointerClick className="h-3 w-3" />{s.clicks || 0}</span>
                  </div>
                </div>
                {s.status !== "active" && <span className="text-[10px] font-bold text-[#c0392b]">PAUSA</span>}
              </button>
            ))}
            {sponsors.length === 0 && <p className="text-sm text-gray-400 px-1">Todavía no hay patrocinadores.</p>}
          </div>
        </section>

        {/* Editor */}
        <section className="lg:col-span-2">
          {!selected ? (
            <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
              <Handshake className="h-10 w-10" />
              <p className="mt-3">Seleccioná o creá un patrocinador para editarlo</p>
            </div>
          ) : (
            <form onSubmit={saveSponsor} key={selected.id} data-testid="sponsor-editor" className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-navy font-bold"><Handshake className="h-5 w-5 text-gold" /> {selected.name}</div>
                <button type="button" onClick={() => deleteSponsor(selected.id)} data-testid="delete-sponsor-btn" className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#c0392b]/5">
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              </div>

              {/* Métricas del sponsor */}
              <div className="mt-4 flex gap-4 text-sm bg-[#eef1f7] rounded-xl px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5 text-navy font-semibold"><Eye className="h-4 w-4 text-gold" /> {selected.impressions || 0} impresiones</span>
                <span className="inline-flex items-center gap-1.5 text-navy font-semibold"><MousePointerClick className="h-4 w-4 text-gold" /> {selected.clicks || 0} clics</span>
                <span className="inline-flex items-center gap-1.5 text-navy font-semibold"><TrendingUp className="h-4 w-4 text-gold" /> CTR {ctr(selected)}</span>
              </div>

              {/* Logo */}
              <div className="mt-4 flex items-center gap-4">
                <div className="h-20 w-32 rounded-xl overflow-hidden border border-gray-100 bg-[#eef1f7] flex items-center justify-center shrink-0">
                  {selected.logo_url ? <img src={selected.logo_url} alt={selected.name} className="max-h-20 w-auto object-contain" /> : <Handshake className="h-8 w-8 text-navy/30" />}
                </div>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy border border-gray-200 rounded-lg px-4 py-2 cursor-pointer hover:border-navy/40">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-gold" />} Subir logo (PNG transparente)
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} disabled={uploading} />
                </label>
              </div>

              {/* Campos */}
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <input name="name" defaultValue={selected.name} placeholder="Nombre" className={field} />
                <select name="tier" defaultValue={selected.tier || "bronce"} className={`bg-white ${field}`}>
                  {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input name="category" defaultValue={selected.category || ""} placeholder="Rubro (ej: Gastronomía)" className={field} />
                <input name="url" defaultValue={selected.url || ""} placeholder="https://sitio-del-sponsor.com" className={field} />
                <select name="status" defaultValue={selected.status || "active"} className={`bg-white ${field}`}>
                  <option value="active">Activo</option>
                  <option value="paused">Pausado</option>
                </select>
                <input name="order" type="number" defaultValue={selected.order ?? 0} placeholder="Orden" className={field} />
                <label className="text-xs font-semibold text-gray-500">Vigente desde
                  <input name="start_date" type="date" defaultValue={selected.start_date || ""} className={`mt-1 ${field}`} />
                </label>
                <label className="text-xs font-semibold text-gray-500">Vigente hasta
                  <input name="end_date" type="date" defaultValue={selected.end_date || ""} className={`mt-1 ${field}`} />
                </label>
                <textarea name="description" defaultValue={selected.description || ""} rows={2} placeholder="Descripción breve" className={`sm:col-span-2 resize-none ${field}`} />
              </div>

              {/* Perfil de empresa (se usa en la página del sponsor cuando no tiene web) */}
              <div className="mt-4 rounded-xl bg-[#f7f8fb] p-4">
                <div className="text-sm font-semibold text-navy">Perfil de la empresa <span className="text-gray-400 font-normal">(si el sponsor no tiene sitio web, el logo abre esta página)</span></div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <input name="phone" defaultValue={selected.phone || ""} placeholder="Teléfono (ej: +54 11 5555-5555)" className={field} />
                  <input name="whatsapp" defaultValue={selected.whatsapp || ""} placeholder="WhatsApp (ej: 5491155555555)" className={field} />
                  <input name="address" defaultValue={selected.address || ""} placeholder="Dirección" className={`sm:col-span-2 ${field}`} />
                  <input name="hours" defaultValue={selected.hours || ""} placeholder="Horarios (ej: Lun a Sáb 9-20h)" className={`sm:col-span-2 ${field}`} />
                  <input name="instagram" defaultValue={selected.instagram || ""} placeholder="Instagram (URL)" className={field} />
                  <input name="facebook" defaultValue={selected.facebook || ""} placeholder="Facebook (URL)" className={field} />
                  <input name="maps_url" defaultValue={selected.maps_url || ""} placeholder="Google Maps embed URL (opcional)" className={`sm:col-span-2 ${field}`} />
                  <textarea name="promotions" defaultValue={selected.promotions || ""} rows={2} placeholder="Promociones actuales" className={`sm:col-span-2 resize-none ${field}`} />
                </div>

                {/* Galería */}
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-navy">Galería de imágenes</span>
                    <label className="inline-flex items-center gap-2 text-xs font-semibold text-navy border border-gray-200 bg-white rounded-lg px-3 py-1.5 cursor-pointer hover:border-navy/40">
                      <Upload className="h-3.5 w-3.5 text-gold" /> Agregar imagen
                      <input type="file" accept="image/*" className="hidden" data-testid="sponsor-gallery-input" onChange={(e) => uploadGallery(e.target.files?.[0])} />
                    </label>
                  </div>
                  {(selected.gallery || []).length > 0 && (
                    <div className="mt-3 flex gap-3 flex-wrap">
                      {selected.gallery.map((img) => (
                        <div key={img} className="relative h-20 w-28 rounded-lg overflow-hidden border border-gray-100 group">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeGalleryImage(img)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Placements */}
              <div className="mt-4">
                <div className="text-sm font-semibold text-navy">¿Dónde se muestra?</div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PLACEMENTS.map((p) => (
                    <label key={p.value} className="inline-flex items-center gap-2 text-sm text-gray-700 bg-[#f7f8fb] rounded-lg px-3 py-2 cursor-pointer">
                      <input type="checkbox" name="placements" value={p.value} defaultChecked={(selected.placements || []).includes(p.value)} data-testid={`sponsor-placement-${p.value}`} className="h-4 w-4 accent-navy" />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              <button data-testid="save-sponsor-btn" disabled={saving} className="mt-5 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar patrocinador
              </button>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
